import { useEffect } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Lens } from "@hookform/lenses"
import { CustomSelect } from "@/components/ui/custom-select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import type { ContentProps } from "@/modules/patients/detail/progress-note/point-of-care/types"

type TimeToTreatContentProps = {
  timeToTreatLens: Lens<PointOfCareFormType["timeToTreatType"]>
  disabled?: boolean
  appInsightsKey?: string
}

export const TimeToTreatContent = (props: TimeToTreatContentProps & ContentProps) => {
  const { timeToTreatLens, appInsightsKey, disabled, timeToTreatData } = props
  const { t } = useTranslation()
  const { control: formControl } = useFormContext<PointOfCareFormType>()
  const timeToTreatType = useWatch({ control: formControl, name: "timeToTreatType" })
  const setConsultationReportTimeToTreat = useConsultationReportStoreContext(
    (state) => state.setConsultationReportTimeToTreat,
  )
  const { control, name } = timeToTreatLens.interop()

  useEffect(() => {
    setConsultationReportTimeToTreat(timeToTreatType ?? null)
  }, [timeToTreatType, setConsultationReportTimeToTreat])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <CustomSelect
          testId="time-to-treat-select"
          onSelect={(selectedTimeToTreatType: string) => {
            field.onChange(selectedTimeToTreatType)
            if (appInsightsKey) {
              FaroInsightsWrapper.pushEvent(appInsightsKey)
            }
          }}
          value={field.value ?? null}
          label={`${t("episodeOfCare.progressNote.pointOfCare.timeToTreat")}*`}
          options={timeToTreatData}
          showValueLabel
          className="w-full @lg/patient-layout:w-1/3"
          error={`${fieldState.error?.message ?? ""}`}
          disabled={disabled}
        />
      )}
    />
  )
}
